import { Adjustments } from "@/core/types"
import { AsShotWB, ColorSpaceCpu, renderCpu, RenderOptions } from "./renderer-cpu"
import { InputColorMatchFeaturesWithFaceData, ReferenceColorMatchFeatures } from "./utils"
import { NeuralColorMatchResult } from "./neural-color-match"
import { vec2, vec3, vec4 } from "gl-matrix"
import { rgbToHsv } from "./color-space"
import { clamp } from "@/core/utils/math"
import {
	getDecreasedValue,
	getLightnessPercentiles,
	getWeightedMean,
	getWeightedMeanRgb,
	getWeightedMedianLuminance,
	logColorMatch,
	step,
	wrapHueDiff,
	wrapHueToLarge,
} from "./utils"
import { getLightness, getPercentileDifference } from "../calibration/ai-calibration-brightness"
import { getHSLDeltaFromDiffs } from "./hsl"
import { applyCorrection } from "./parameters"
import { Sentry } from "@/telemetry/sentry"

/**
 * Main face tone correction for color match (half path).
 *
 * The neural LUT adjusts global color, but face skin often ends up with
 * unnatural hue/saturation/luminance because the model optimises for the
 * whole image. This function iteratively corrects face tone using HSL orange
 * channel + exposure/shadows/highlights etc. adjustments.
 *
 * ### Design rationale
 *
 * The rules here were developed empirically by testing on many diverse
 * datasets (varying skin tones, lighting, race combinations, false positive
 * face detections, etc.). Each threshold and branch addresses a specific
 * failure case observed in practice — e.g., dark skin getting washed out,
 * red-shifted faces from WB correction, over-exposed faces from shadow
 * recovery, or race-mismatch artifacts when ref and input have very different
 * skin tones. The logic is intentionally conservative with many clamps to
 * avoid making things worse.
 *
 * ### How to tune for a new bad case
 *
 * 1. Use a color picker tool (e.g. https://pinetools.com/image-color-picker)
 *    to sample the face region's HSV/HSL in the problematic output image.
 * 2. Compare with the input face HSV and reference face HSV (logged by
 *    `logColorMatch("[face tone] faceHSV", ...)`).
 * 3. Identify which channel (hue/sat/lum) is off and in which direction —
 *    e.g. "face is too red" = hue too low, "face is washed out" = sat too low.
 * 4. Trace through `getFaceTargets` to see which branch produced the wrong
 *    target, then adjust the threshold/clamp/weight for that specific case.
 * 5. Re-test on existing datasets to check for regressions — the many clamps
 *    are there precisely because fixing one case can break another.
 *
 * A longer-term improvement: collect (beforeCorrection, afterCorrection)
 * pairs and train a small model to replace `getFaceTargets` directly.
 *
 * ### Pipeline (5 steps)
 *
 * 1. **Normalize face exposure** — if the face is much darker/brighter than
 *    expected (compared to population statistics), nudge exposure/shadows so
 *    subsequent hue adjustments aren't biased by extreme luminance.
 *
 * 2. **Align to LUT face result** — the LUT produces a "luted" face color
 *    that is usually a reasonable target. If the parameter-estimated face
 *    color diverges too much, pull it closer via HSL orange adjustments.
 *
 * 3. **Apply global corrections** — vegetation color, WB, and saturation
 *    compensation (via `applyCorrection`). Placed here so face-specific
 *    adjustments from steps 1–2 are incorporated before global corrections,
 *    and the final face refinement (step 5) happens after.
 *    Also detects likely false-positive face detection and bails out early.
 *
 * 4. **Compute face HSV targets** — determines target hue, saturation, and
 *    luminance by blending reference face color, input face color, and
 *    after-adjustment face color, with guards for dark skin, dissimilar
 *    races, and false positives. (See `getFaceTargets`.)
 *
 * 5. **Iterative HSL convergence** — adjusts hueOrange, saturationOrange,
 *    exposure, shadows, highlights, whites iteratively (2–3 iterations)
 *    to converge the actual face HSVL toward the targets from step 4.
 *    Each iteration re-renders face pixels to measure progress.
 *
 * Finally, if hslScale ≠ 1, the orange HSL deltas are scaled down (used
 * for member photos in a cluster where the key photo is a reference).
 */
export function adjustFaceTone(options: {
	inputFeatures: InputColorMatchFeaturesWithFaceData
	referenceFeatures: ReferenceColorMatchFeatures
	adjustments: Partial<Adjustments>
	baseRenderOptions: RenderOptions
	faceBefore: Float32Array | null
	faceAfter: Float32Array | null
	faceWeights: Float32Array | null
	hslScale: number
	srgbBefore: Float32Array
	maxExposure: number
	isForAIStyle?: boolean
	satCorrectionOptions: {
		baseAdjustments: Partial<Adjustments> | undefined
		inputPreset: Float32Array
		inputAfter: Float32Array
		luts?: NeuralColorMatchResult
		lightStrength: number
		colorStrength: number
		pixelWeights: number[]
	}
}): Partial<Adjustments> {
	const {
		inputFeatures,
		referenceFeatures,
		adjustments,
		baseRenderOptions,
		faceBefore,
		faceAfter,
		faceWeights,
		hslScale,
		srgbBefore,
		maxExposure,
		satCorrectionOptions,
		isForAIStyle = true,
	} = options

	let result = { ...adjustments }
	if (
		!inputFeatures.avgFaceHsvl ||
		!faceBefore ||
		!faceWeights ||
		!faceAfter ||
		faceBefore.length === 0 ||
		faceAfter.length === 0 ||
		faceWeights.length === 0
	) {
		if (inputFeatures.avgFaceHsvl) {
			const errorData = {
				faceBefore,
				faceAfter,
				faceWeights,
				inputFeatures,
			}
			Sentry.captureException(
				new Error("[Color Match] Face pixels data missing while face hsvl is available"),
				{
					extra: errorData,
				},
			)
		}
		return applyCorrection({
			adjustments: result,
			referenceFeatures,
			baseRenderOptions,
			srgbBefore,
			...satCorrectionOptions,
		})
	}

	const hasBaseAdjustments = !!satCorrectionOptions.baseAdjustments

	let afterFaceHSVL = getAfterFaceHSVL(faceBefore, faceWeights, adjustments, baseRenderOptions)
	let needRecomputeFaceHSVL = false
	const originalAfterLum = afterFaceHSVL[3]
	logColorMatch("[face tone] initial afterFaceHSVL", [
		afterFaceHSVL[0] * 360,
		afterFaceHSVL[1],
		originalAfterLum,
	])

	// 1: try to standardize face exposure before adjusting face to LUT because lighting could affect face hue
	const newResultLighting = normalizeFaceExposure({
		faceBefore,
		faceWeights,
		adjustments: result,
		baseRenderOptions,
		afterFaceHSVL,
		refFaceHSVL: referenceFeatures.avgFaceHsvl,
		maxExposure,
		inputFaceHSVL: inputFeatures.avgFaceHsvl,
	})
	if (newResultLighting) {
		result = newResultLighting
		afterFaceHSVL = getAfterFaceHSVL(faceBefore, faceWeights, result, baseRenderOptions)
		needRecomputeFaceHSVL = true
	}

	// 2: adjust face tone if it's too different from the LUT face tone result (usually reasonable);
	// potential reason: the parameters estimation sacrifices face tone for other larger regions
	const { result: newResultLut, lutedFaceHSVL } = adjustFaceToneToLutResult({
		faceBefore,
		faceWeights,
		faceAfter,
		afterFaceHSVL,
		adjustments: result,
		baseRenderOptions,
		adjustValue: false,
		isForAIStyle,
		inputFaceHSVL: inputFeatures.avgFaceHsvl,
	})
	if (newResultLut) {
		result = newResultLut
		needRecomputeFaceHSVL = true
	}

	// 3. apply correction (WB + HSL vegetation color correction and saturation compensation) after matching to lut result but before face tone adjustment

	// likely false positive face detection
	const isFalseFace = lutedFaceHSVL && !isReasonableFaceColor(lutedFaceHSVL)
	result = applyCorrection({
		adjustments: isFalseFace ? adjustments : result,
		referenceFeatures,
		baseRenderOptions,
		srgbBefore,
		...satCorrectionOptions,
	})
	if (isFalseFace) {
		logColorMatch("[face tone] is false face", lutedFaceHSVL)
		return result
	}

	if (needRecomputeFaceHSVL) {
		afterFaceHSVL = getAfterFaceHSVL(faceBefore, faceWeights, result, baseRenderOptions)
	}

	// if face is too desaturated, increase saturation until it reaches original base saturation + 0.05
	if (afterFaceHSVL[1] < 0.2) {
		const originalSat = (satCorrectionOptions.baseAdjustments?.saturation ?? 0) + 0.05
		const satChange = (result.saturation ?? 0) - originalSat
		if (satChange < 0) {
			const deltaSat = (0.2 - afterFaceHSVL[1]) * 3
			result.saturation = clamp((result.saturation ?? 0) + deltaSat, -1, originalSat)
			afterFaceHSVL = getAfterFaceHSVL(faceBefore, faceWeights, result, baseRenderOptions)
		}
	}

	// determine if the reference face is similar to the input face
	let refIsSimilar = true
	if (referenceFeatures.avgFaceHsvl) {
		const satDiff = referenceFeatures.avgFaceHsvl[1] - afterFaceHSVL[1]
		const valDiff = referenceFeatures.avgFaceHsvl[3] - afterFaceHSVL[3]
		const inputSat = inputFeatures.avgFaceHsvl[1]
		const refSat = referenceFeatures.avgFaceHsvl[1]

		// reference is very different from input image's face tone (after adjustments)
		// probably different races
		// inputSat > 0.7, input is likely a dark skin
		if (
			satDiff > 0.25 ||
			satDiff < -0.3 ||
			Math.abs(valDiff) > 0.45 ||
			(inputSat > 0.7 && refSat < clamp(inputSat - 0.22, 0.58, 0.65))
		) {
			refIsSimilar = false
		}
	}
	logColorMatch("[face tone] faceHSV", {
		refHSVL: [
			(referenceFeatures?.avgFaceHsvl?.[0] ?? 0) * 360,
			referenceFeatures?.avgFaceHsvl?.[1] ?? 0,
			referenceFeatures?.avgFaceHsvl?.[2] ?? 0,
			referenceFeatures?.avgFaceHsvl?.[3] ?? 0,
		],
		inputFaceHSVL: [
			inputFeatures.avgFaceHsvl[0] * 360,
			inputFeatures.avgFaceHsvl[1],
			inputFeatures.avgFaceHsvl[2],
			inputFeatures.avgFaceHsvl[3],
		],
		afterFaceHSVL: [
			afterFaceHSVL[0] * 360,
			afterFaceHSVL[1],
			afterFaceHSVL[2],
			afterFaceHSVL[3],
		],
		refIsSimilar,
	})

	// 4. This is the most important!! part of adjusting face tone: get the face HSV targets that are closer to the reference and also close to the original for natural result
	const { targetHue, targetSat, targetLum, iters } = getFaceTargets({
		inputFaceHSVL: inputFeatures.avgFaceHsvl,
		referenceFaceHSVL: referenceFeatures.avgFaceHsvl,
		afterFaceHSVL,
		refIsSimilar,
		isForAIStyle,
		originalAfterLum,
		adjustments,
		result,
		asShotWB: {
			asShotTemp: baseRenderOptions.asShotTemp,
			asShotTint: baseRenderOptions.asShotTint,
		},
	})

	logColorMatch("[face tone] face target", {
		targetHue: targetHue * 360,
		targetSat,
		targetLum,
	})

	// 5. adjust face tone iteratively to the target HSV values to get the final result
	for (let iter = 0; iter < iters; iter++) {
		const [afterHue, afterSaturation, _, afterLuminance] = afterFaceHSVL

		const hueDiff = wrapHueDiff(targetHue - afterHue)
		const satDiff = targetSat - afterSaturation
		const valDiff = targetLum - afterLuminance

		if (Math.abs(hueDiff) < 0.001 && Math.abs(satDiff) < 0.002 && Math.abs(valDiff) < 0.002) {
			logColorMatch("[face tone iter] converged, break at iter", iter)
			break
		}

		const scale = 0.7
		const finalHueScale = 0.9
		const hueLimit = 0.14
		const satLimit = 0.18
		const valLimit = 0.6

		let [deltaHue, deltaSaturation, deltaValue] = getHSLDeltaFromDiffs({
			hsvDiff: [hueDiff, satDiff, valDiff],
			color: "orange",
			scale,
			satLumScale: valDiff < 0 ? 0.06 : 0.06 * (1 - step(targetSat, 0.2, 0.4)),
			finalHueScale,
		})

		// face is too reddish, reduce tint and use smaller orange scale
		let orangeHueScale = 1
		if ((afterHue * 360 < 4 || afterHue * 360 > 230) && hueDiff > 0.0) {
			const afterHueLarge = wrapHueToLarge(afterHue)
			orangeHueScale = 1.0 + Math.min((afterHueLarge - 1.04) * 5, 0)
			const tintScale = clamp((afterSaturation - 0.17) / 0.1, 0, 1)
			const deltaTint = clamp(-0.6 * hueDiff * 360, -10, 10) * tintScale
			logColorMatch("[face tone iter] deltaTint due to red face", {
				deltaTint,
				tintScale,
				afterSaturation,
			})
			result.tint = clampToRange(
				(result.tint ?? 0) + deltaTint,
				adjustments.tint,
				[-25, 25],
				100,
			)

			if (afterSaturation < 0.17) {
				const tempScale = clamp((0.17 - afterSaturation) / 0.07, 0, 1)
				const deltaTemp = clamp(hueDiff * 360 * 3, 0, 100) * tempScale
				result.temperature = (result.temperature ?? 0) + deltaTemp
				logColorMatch("[face tone iter] deltaTemp due to low sat red face", {
					deltaTemp,
					tempScale,
					afterSaturation,
				})
			}
		}

		// Hue
		const maxHueDelta = hasBaseAdjustments ? 0.15 : 0.2
		const maxHueDeltaPositive = Math.max(0 - (adjustments.hueOrange ?? 0), maxHueDelta)
		const maxHueDeltaNegative = Math.min(0 - (adjustments.hueOrange ?? 0), -maxHueDelta)
		deltaHue = getDecreasedValue(deltaHue * orangeHueScale, hueLimit, 0.4)
		result.hueOrange = clampToRange((result.hueOrange ?? 0) + deltaHue, adjustments.hueOrange, [
			maxHueDeltaNegative,
			maxHueDeltaPositive,
		])

		// Saturation
		const maxSatDelta = hasBaseAdjustments ? 0.15 : 0.2
		const maxSatDeltaPositive = Math.max(0 - (adjustments.saturationOrange ?? 0), maxSatDelta)
		const maxSatDeltaNegative = Math.min(0 - (adjustments.saturationOrange ?? 0), -maxSatDelta)
		deltaSaturation = getDecreasedValue(deltaSaturation * 0.8, satLimit, 0.5)
		deltaSaturation = clamp(deltaSaturation, maxSatDeltaNegative, maxSatDeltaPositive)
		const initialSaturationOrange = adjustments.saturationOrange ?? 0
		const maxSaturationDelta = Math.max(
			0,
			Math.min(initialSaturationOrange + maxSatDeltaPositive, 0.3) - initialSaturationOrange,
		)
		const minSaturationDelta = Math.min(
			0,
			Math.max(initialSaturationOrange + maxSatDeltaNegative, -0.3) - initialSaturationOrange,
		)
		result.saturationOrange = clampToRange(
			(result.saturationOrange ?? 0) + deltaSaturation,
			initialSaturationOrange,
			[minSaturationDelta, maxSaturationDelta],
		)

		// Luminance
		deltaValue /= scale // don't use scale for luminance
		deltaValue = getDecreasedValue(deltaValue, valLimit, 0.4)
		if (iter > 0) {
			deltaValue = clamp(deltaValue, -0.2, 0.2)
		}
		const valScale = deltaValue > 0 ? 0.8 : 1
		let deltaExposure = deltaValue * 0.5 * valScale
		if (afterLuminance > 0.75) {
			const deltaHighlights = deltaValue * 0.35 * valScale
			deltaExposure = deltaValue * 0.35 * valScale
			result.highlights = clampToRange(
				(result.highlights ?? 0) + deltaHighlights,
				adjustments.highlights,
				[-0.4, 0.2],
			)
			result.whites = clampToRange(
				(result.whites ?? 0) + deltaHighlights * 0.9,
				adjustments.whites,
				[-0.4, 0.2],
			)
		} else if (afterLuminance < 0.45 || ((result.shadows ?? 0) < 0 && deltaValue > 0)) {
			const deltaShadows = deltaValue * 0.25 * valScale
			deltaExposure = deltaValue * 0.2 * valScale
			result.shadows = (result.shadows ?? 0) + deltaShadows
			result.contrast = (result.contrast ?? 0) + deltaShadows * 0.8
			result.shadows = clampToRange(result.shadows, adjustments.shadows, [-0.2, 0.35])
			result.contrast = clampToRange(result.contrast, adjustments.contrast, [-0.16, 0.24])

			// blacks for extremely dark skin
			if (afterLuminance < 0.3) {
				const deltaBlacks = (0.3 - afterLuminance) * 2.5
				result.blacks = clamp((result.blacks ?? 0) + deltaBlacks, -1, 1)
			}
		}
		const targetExposure = (result.exposure ?? 0) + deltaExposure
		result.exposure = clampToRange(
			Math.min(maxExposure, targetExposure),
			adjustments.exposure,
			[-0.25, 0.15],
			5,
		)
		if (targetExposure < result.exposure) {
			const deltaExposure = targetExposure - result.exposure
			const deltaHighlights = deltaExposure * 1.6
			logColorMatch("[face tone iter] result.exposure too high, reduce highlights", {
				deltaHighlights,
				targetExposure,
				resultExposure: result.exposure,
				maxExposure,
			})
			result.highlights = clampToRange(
				(result.highlights ?? 0) + deltaHighlights,
				adjustments.highlights,
				[-0.4, 0.2],
			)
			result.whites = clampToRange(
				(result.whites ?? 0) + deltaHighlights * 0.9,
				adjustments.whites,
				[-0.4, 0.2],
			)
		} else if (targetExposure > result.exposure) {
			const deltaExposure = targetExposure - result.exposure
			const deltaShadows = deltaExposure * 0.8
			logColorMatch("[face tone iter] result.exposure too low, increase shadows", {
				deltaShadows,
				targetExposure,
				resultExposure: result.exposure,
				maxExposure,
			})
			result.shadows = (result.shadows ?? 0) + deltaShadows
			result.contrast = (result.contrast ?? 0) + deltaShadows * 0.8
			result.shadows = clampToRange(result.shadows, adjustments.shadows, [-0.2, 0.35])
			result.contrast = clampToRange(result.contrast, adjustments.contrast, [-0.16, 0.24])
		}

		logColorMatch("[face tone iter] deltaHsv, iter", iter, {
			deltaHue,
			deltaSaturation,
			deltaValue,
		})

		if (iter < iters - 1) {
			afterFaceHSVL = getAfterFaceHSVL(faceBefore, faceWeights, result, baseRenderOptions)
			logColorMatch("[face tone iter] afterFaceHSVL, iter", iter + 1, {
				afterFaceHSVL: [
					afterFaceHSVL[0] * 360,
					afterFaceHSVL[1],
					afterFaceHSVL[2],
					afterFaceHSVL[3],
				],
			})
		}
	}

	if (hslScale != 1) {
		result.hueOrange =
			((result.hueOrange ?? 0) - (adjustments.hueOrange ?? 0)) * hslScale +
			(adjustments.hueOrange ?? 0)
		result.saturationOrange =
			((result.saturationOrange ?? 0) - (adjustments.saturationOrange ?? 0)) * hslScale +
			(adjustments.saturationOrange ?? 0)
		// result.luminanceOrange = ((result.luminanceOrange ?? 0) - (adjustments.luminanceOrange ?? 0)) * hslScale + (adjustments.luminanceOrange ?? 0)
	} else {
		const deltaHue = (result.hueOrange ?? 0) - (adjustments.hueOrange ?? 0)
		const sign = Math.sign(deltaHue * (adjustments.hueOrange ?? 0))
		// try to prevent the hue adjustments further deviating from zero for natural result
		if (sign > 0) {
			result.hueOrange =
				((result.hueOrange ?? 0) - (adjustments.hueOrange ?? 0)) * 0.6 +
				(adjustments.hueOrange ?? 0)
		}
	}

	return result
}

/**
 * Determine target hue / saturation / luminance for face tone correction.
 *
 * The goal is to find HSV targets that are:
 * - Close to the reference face (style transfer)
 * - Close to the input face (natural, avoids race mismatch artifacts)
 * - Within "reasonable skin color" bounds
 *
 * Each channel has its own strategy:
 *
 * **Luminance**: bounded by [minLum, maxLum] derived from the reference, with
 * special handling for dark skin (lower floor). Avoids over-exposing faces
 * that are already bright, and caps the delta from the original after-lum.
 *
 * **Saturation**: bounded by the input's original saturation ± offsets, pulled
 * toward the reference when similar. Dark skin gets saturation-luminance
 * coupling (brighter → less saturated to look natural). False-positive
 * detections (very low sat) have limited sat increase.
 *
 * **Hue**: most complex — blends after-hue, reference-hue, and input-hue with
 * weights that increase toward the input hue proportionally to how much
 * temperature/tint/hueOrange has already been changed (preserving original
 * when adjustments are large). High-saturation faces are pushed toward
 * orange to avoid an unnatural red look. Dark skin gets separate hue/sat
 * coupling adjustments.
 *
 * Returns number of iterations (2 normally, 3 for over-exposed faces).
 */
function getFaceTargets(options: {
	inputFaceHSVL: vec4
	referenceFaceHSVL: vec4 | null
	afterFaceHSVL: vec4
	refIsSimilar: boolean
	isForAIStyle?: boolean
	originalAfterLum: number
	adjustments: Partial<Adjustments>
	result: Partial<Adjustments>
	asShotWB: AsShotWB
}) {
	const {
		inputFaceHSVL,
		referenceFaceHSVL,
		afterFaceHSVL,
		refIsSimilar,
		isForAIStyle,
		originalAfterLum,
		adjustments,
		result,
		asShotWB,
	} = options
	let targetHue = 0
	let targetSat = 0
	let targetLum = 0
	const afterLum = afterFaceHSVL[3]
	const lutedHue = wrapHueToLarge(referenceFaceHSVL?.[0] ?? afterFaceHSVL[0])
	const isInputFaceProblematic = isFaceProblematic(inputFaceHSVL) // identify problematic input face skin color (e.g. too red) to not use them

	const isDarkSkin = isInputFaceProblematic
		? false
		: inputFaceHSVL[1] > 0.7 ||
			(isFaceDarkSkin(inputFaceHSVL) &&
				((isFaceDarkSkin(referenceFaceHSVL) && refIsSimilar) ||
					isFaceDarkSkin(afterFaceHSVL)))
	logColorMatch("!!isDarkSkin", isDarkSkin, "isInputFaceProblematic", isInputFaceProblematic)

	let iters = 2

	// 1. Luminance **********

	// minLum 0.58 -> 0.5 as auto light step already adjusted most of the face lighting, so use a wider range
	let maxLum = 0.68
	let minLum = 0.5
	const darkSkinMinLum = 0.38
	let refLum = clamp(afterLum, minLum, maxLum)
	const inputAfterSat = afterFaceHSVL[1] * 0.7 + inputFaceHSVL[1] * 0.3
	if (referenceFaceHSVL) {
		maxLum = clamp(referenceFaceHSVL[3], 0.62, 0.7)
		minLum = clamp(referenceFaceHSVL[3], isDarkSkin ? darkSkinMinLum : 0.47, 0.53)
		let minLumRef = Math.min(inputFaceHSVL[3], originalAfterLum) - 0.05
		minLumRef = Math.min(Math.max(minLumRef, minLum), afterLum, maxLum)
		if (refIsSimilar) refLum = clamp(referenceFaceHSVL[3], minLumRef, maxLum)
		else if (isDarkSkin) {
			// dark skin
			refLum = Math.max(referenceFaceHSVL[3], inputFaceHSVL[3])
			refLum = Math.min(refLum, afterLum)
		}
	} else if (isDarkSkin) {
		// dark skin
		refLum = Math.max(inputFaceHSVL[3], afterLum - 0.1, darkSkinMinLum)
		refLum = Math.min(refLum, afterLum)
	}
	if (isDarkSkin) {
		minLum = Math.min(minLum, darkSkinMinLum)
	} else if (inputAfterSat < 0.35) {
		// lower sat skin should be brighter to be natural
		minLum = clamp(
			0.48 + (0.35 - inputAfterSat) * 0.7 + wrapHueDiff(15 / 360 - targetHue),
			minLum,
			0.55,
		)
	}
	targetLum = clamp(afterLum, minLum, maxLum) * 0.55 + refLum * 0.25 + afterLum * 0.2
	if (targetLum > Math.max(inputFaceHSVL[3], refLum)) {
		targetLum = Math.max(inputFaceHSVL[3], refLum) * 0.5 + targetLum * 0.5
		logColorMatch("!!targetLum adjusted for small input and ref", targetLum)
	}
	// if target is brighter than after, check input because we don't want to over-expose
	if (targetLum > afterLum) {
		const increasedLighting =
			(result.exposure ?? 0) -
			(adjustments.exposure ?? 0) +
			(result.shadows ?? 0) -
			(adjustments.shadows ?? 0)
		// if lighting is already increased, but still afterLum is dark, possibly part of the face is too dark and we shouldn't increase lum too much
		const deltaLumInput = clamp(0.25 - increasedLighting * 0.3, 0.2, 0.25)
		logColorMatch("increasedLighting", {
			increasedLighting,
			deltaLumInput,
			result,
			adjustments,
		})
		const maxLum = Math.max(afterLum, inputFaceHSVL[3] + deltaLumInput)
		const clampedAfterLum = Math.max(afterLum, 0.56)
		targetLum = Math.min(targetLum, maxLum, clampedAfterLum)
	}
	let deltaLum = targetLum - afterLum
	deltaLum = getDecreasedValue(deltaLum, 0.1, 0.5)
	targetLum = clamp(afterLum + deltaLum, originalAfterLum - 0.12, originalAfterLum + 0.12)

	// 2. Saturation **********

	const afterSat = afterFaceHSVL[1]
	const minSat = 0.25 + clamp(targetLum - 0.65, 0, 0.1)
	const orangeHue = targetHue > 0 && targetHue < 100 / 360 ? targetHue * 360 - 15 : 0
	const orangeHueFactor = clamp(orangeHue * 0.01, 0, 0.15)
	const darkSkinFactor = clamp(0.3 - afterLum + afterSat - 0.7, 0, 0.15)
	const darkSkinFactor2 = clamp(inputFaceHSVL[1] - 0.65, 0, 0.15)
	const clampedOriginalSat = isInputFaceProblematic
		? afterSat
		: clamp(
				inputFaceHSVL[1],
				0.2,
				0.55 + Math.max(orangeHueFactor, darkSkinFactor, darkSkinFactor2),
			)
	targetSat = clamp(
		afterSat,
		Math.max(Math.min(minSat, inputFaceHSVL[1]), clampedOriginalSat - 0.3),
		Math.max(0.4, clampedOriginalSat + 0.15),
	)
	let targetScale = 0.5
	if (isForAIStyle) {
		targetScale = 0.7
	}
	let satRef = referenceFaceHSVL?.[1] ?? 0
	if (referenceFaceHSVL && refIsSimilar) {
		if (isDarkSkin) {
			const deltaLum = getDecreasedValue(targetLum - referenceFaceHSVL[3], 0.06, 0.3)
			// higher lum, lower sat to make color actually look similar for darker skin
			satRef = satRef - clamp(deltaLum, -0.12, 0.12)
			logColorMatch("!!refSatAdjusted", -deltaLum)
		}
		if (targetSat > satRef && targetSat > clampedOriginalSat) {
			targetSat = Math.max(satRef, clampedOriginalSat)
			// targetScale = 1
		} else if (targetSat < satRef && targetSat < clampedOriginalSat) {
			targetSat = Math.min(satRef, clampedOriginalSat)
			// targetScale = 1
		}
	} else {
		targetSat = targetSat * 0.7 + clampedOriginalSat * 0.3
	}
	targetSat = clamp(
		targetSat * targetScale + afterSat * (1 - targetScale),
		afterSat - 0.25,
		afterSat + 0.15,
	)
	if (inputFaceHSVL[1] < 0.12) {
		// likely false positive face detection, try to limit sat increase
		targetSat = Math.min(targetSat, Math.max(afterSat, inputFaceHSVL[1] * 2))
	}

	// 3. Hue **********

	let hueInput = wrapHueToLarge(inputFaceHSVL[0])
	const hueAfter = wrapHueToLarge(afterFaceHSVL[0])
	let clampedAfterHue = clamp(hueAfter, 1 + 4 / 360, 1 + 28 / 360)

	hueInput = getAdjustedHue({
		hue: hueInput,
		sat: inputFaceHSVL[1],
		newSat: targetSat,
		scale: isDarkSkin ? 0.8 : 0.4,
	})
	logColorMatch("[target hue] inputHue adjusted for sat change", (hueInput * 360) % 360)
	if (isInputFaceProblematic) {
		hueInput = hueAfter
	}

	let minHue = hueAfter - 10 / 360
	if (targetSat > 0.3) {
		// avoid too red for higher sat face
		const minHueRed = Math.min(12 + (targetSat - 0.3) * 40, 20) / 360 + 1
		logColorMatch(
			"[target hue] minHueRed",
			(minHueRed - 1) * 360,
			"minHue",
			(minHue - 1) * 360,
			"hueAfter",
			(hueAfter - 1) * 360,
		)
		minHue = Math.min(Math.max(minHueRed, minHue), hueAfter)
	}
	const clampedInputHue = clamp(hueInput, minHue, hueAfter + 10 / 360)

	// The more changes in hue, temperature and tint, the more we should preserve the original hue
	const tempChange =
		((result.temperature ?? asShotWB.asShotTemp) - asShotWB.asShotTemp) / asShotWB.asShotTemp
	const tintChange = ((result.tint ?? asShotWB.asShotTint) - asShotWB.asShotTint) / 100
	const changedFactor =
		0.1 +
		Math.min(Math.abs(result.hueOrange ?? 0) + Math.abs(tempChange) + Math.abs(tintChange), 0.2) // 0 - 0.3

	if (referenceFaceHSVL && refIsSimilar) {
		let hueRef = wrapHueToLarge(referenceFaceHSVL[0])
		hueRef = getAdjustedHue({
			hue: hueRef,
			sat: referenceFaceHSVL[1],
			newSat: targetSat,
			scale: isDarkSkin ? 0.8 : 0.4,
		})
		if (clampedAfterHue > hueRef && clampedAfterHue > hueInput) {
			clampedAfterHue = Math.max(hueRef, hueInput)
		} else if (clampedAfterHue < hueRef && clampedAfterHue < hueInput) {
			clampedAfterHue = Math.min(hueRef, hueInput)
		}
		const preserveFactor = 0.3 - changedFactor * 0.7 // 0.09 - 0.3
		targetHue =
			(clampedAfterHue * (1 - preserveFactor) + hueAfter * preserveFactor) *
				(1 - changedFactor) +
			clampedInputHue * changedFactor
	} else {
		const inputFactor = 0.15 + changedFactor * 0.5 // 0.15 - 0.3
		targetHue =
			((clampedAfterHue * 0.55 + hueAfter * 0.15) / 0.7) * (1 - inputFactor) +
			clampedInputHue * inputFactor
	}
	const deltaHue = getDecreasedValue(targetHue - hueAfter, 4 / 360, 0.4)
	targetHue = hueAfter + deltaHue
	logColorMatch("[target hue] targetHue", {
		targetHue: (targetHue * 360) % 360,
		changedFactor,
		clampedInputHue: (clampedInputHue * 360) % 360,
		hueAfter: (hueAfter * 360) % 360,
	})

	// If hue is too red (< 360), clamp it to 0
	if (targetHue < 1 && hueInput > 1) {
		targetHue = 0
	}
	if (lutedHue > 1 + 35 / 360) {
		// potential false positive face detection, try to limit hue change
		targetHue = clamp(
			targetHue,
			Math.min(hueAfter, lutedHue - 5 / 360),
			Math.max(hueAfter, lutedHue + 5 / 360),
		)
	}

	targetHue = targetHue % 1

	// Further Adjustments
	// if saturation is high, we should adjust hue to be more orange, otherwise the face looks red (mainly for white skin)
	const aveSat = (targetSat + afterSat) / 2
	if (aveSat > 0.45) {
		const targetHueLarge = wrapHueToLarge(targetHue)
		const increasedInputHue = clamp((0.45 - inputFaceHSVL[1]) * 20, -4, 6)
		let minHue =
			Math.min(
				22 + 360,
				(aveSat - 0.45) * 80 + 15 + 360, // not too red with high saturation
				targetHueLarge * 360 + 7, // not too different from target hue
				hueInput * 360 + increasedInputHue, // also not too different from original in case bad face detection or unnatural face
			) / 360
		minHue = Math.max(minHue, 15 / 360 + 1)
		const clampedTargetHue = Math.max(targetHueLarge, minHue)

		let hueRef = -1000
		if (referenceFaceHSVL && refIsSimilar) {
			hueRef = wrapHueToLarge(referenceFaceHSVL[0])
		}
		if (hueRef > minHue) {
			targetHue = hueRef * 0.2 + clampedTargetHue * 0.7 + targetHueLarge * 0.1
		} else if (hueRef > -1000) {
			targetHue = Math.max(
				hueRef * 0.3 + clampedTargetHue * 0.5 + targetHueLarge * 0.2,
				targetHueLarge,
			)
		} else {
			targetHue = clampedTargetHue * 0.8 + targetHueLarge * 0.2
		}

		// for dark skin, reduce sat to match ref better, and adjust hue to be more red for smaller sat
		if (isDarkSkin && refIsSimilar && satRef > 0 && targetSat > satRef) {
			const newSat = clamp(targetSat * 0.4 + satRef * 0.6, targetSat - 0.06, targetSat)
			const satReduction = newSat - targetSat
			if (targetHue > clampedAfterHue) {
				targetHue = Math.max(targetHue + (satReduction * 60) / 360, clampedAfterHue)
			}
			logColorMatch("!!sat reduction", satReduction)
			targetSat = newSat
		}

		targetHue = targetHue % 1
	}
	// if sat is relatively high, and hue will be shift to red, we should reduce sat
	if (targetSat > afterSat && targetSat > 0.35 && deltaHue < -1 / 360) {
		targetSat = Math.max(afterSat, targetSat + clamp(deltaHue + 1 / 360, -0.02, 0.0))
	}

	// more iterations for over-exposed face
	if (afterLum > 0.62) iters = 3

	return {
		targetHue,
		targetSat,
		targetLum,
		iters,
	}
}

/** Detect problematic input face color (extreme saturation or unreasonable hue) to avoid using it as a target. */
export function isFaceProblematic(inputFaceHSVL: vec4 | null) {
	if (!inputFaceHSVL) {
		return false
	}

	if (
		((inputFaceHSVL[0] < 12 / 360 || inputFaceHSVL[0] > 40 / 360) && inputFaceHSVL[1] > 0.8) ||
		((inputFaceHSVL[0] < 15 / 360 || inputFaceHSVL[0] > 40 / 360) && inputFaceHSVL[1] > 0.9) ||
		!isReasonableFaceColor(inputFaceHSVL)
	) {
		return true
	}

	return false
}

/** Heuristic for dark skin: high saturation or large sat-lum gap. */
export function isFaceDarkSkin(faceHSVL: vec4 | null) {
	if (!faceHSVL) {
		return false
	}

	return faceHSVL[1] > 0.7 || faceHSVL[1] - faceHSVL[3] > 0.55
}

/** Compensate hue for saturation change — higher sat shifts hue toward orange,
 * lower sat shifts toward red. This coupling makes skin look natural. */
function getAdjustedHue({
	hue,
	sat,
	newSat,
	scale = 1,
}: {
	hue: number
	sat: number
	newSat: number
	scale?: number
}): number {
	const satChange = newSat - sat
	const relativeSatChange = Math.abs(satChange) / sat
	if (relativeSatChange > 0.2) {
		const deltaSat = clamp(relativeSatChange - 0.2, 0, 0.15) * Math.sign(satChange)
		hue = hue + ((deltaSat * 60) / 360) * scale
	}
	return hue
}

/** Render face pixels with current adjustments and return weighted-mean HSVL. */
function getAfterFaceHSVL(
	faceBefore: Float32Array,
	faceWeights: Float32Array,
	adjustments: Partial<Adjustments>,
	baseRenderOptions: RenderOptions,
): vec4 {
	const afterFacePixels = renderCpu(faceBefore, adjustments, {
		inputColorSpace: ColorSpaceCpu.sRGB,
		outColorSpace: ColorSpaceCpu.sRGB,
		...baseRenderOptions,
	})

	return getFaceHSVL(afterFacePixels, faceWeights)
}

export function getFaceHSVL(faceAfter: Float32Array, faceWeights: Float32Array): vec4 {
	const avgRGBLuted = getWeightedMeanRgb(faceAfter, faceWeights) || [0, 0, 0]
	const avgHsvLuted = vec3.create()
	rgbToHsv(avgHsvLuted, avgRGBLuted)
	//const avgLumLuted = (Math.max(...avgRGBLuted) + Math.min(...avgRGBLuted)) / 2
	const avgLumLuted = getWeightedMedianLuminance(faceAfter, faceWeights)

	return [avgHsvLuted[0], avgHsvLuted[1], avgHsvLuted[2], avgLumLuted]
}

/** Compute face-specific auto-exposure using percentile-difference against population means
 * (same approach as `getAutoExposure` in ai-calibration-brightness, but face-only). */
function getFaceExposure(pixels: Float32Array, faceWeights: Float32Array) {
	const scale = 3

	// prettier-ignore
	const means = [
		0.399, 0.543, 0.665, 0.74, 0.806, 0.842, 0.893, 0.648 // face
	]
	// prettier-ignore
	const stds = [
		0.101, 0.093, 0.079, 0.068, 0.064, 0.064, 0.065, 0.068
	]

	const lum = new Float32Array(getLightness(pixels, 3, 1))
	//const histogram = computeHistogram(lum)
	const faceLum = getWeightedMean(lum, faceWeights)
	//const facePercentiles = computePercentiles(histogram)
	const facePercentiles = getLightnessPercentiles(
		lum,
		faceWeights,
		[0.05, 0.2, 0.5, 0.75, 0.9, 0.95, 0.99],
	)

	const maxDiff = getPercentileDifference(
		facePercentiles.concat(faceLum),
		means,
		stds,
		0.02,
		[-1],
	)

	const maxDiffLow = getPercentileDifference(
		facePercentiles.concat(faceLum),
		means,
		stds,
		0.02,
		[4, 5, 6],
	)

	const maxDiffHigh = getPercentileDifference(
		facePercentiles.concat(faceLum),
		means,
		stds,
		0.02,
		[0, 1, 2, 3],
	)

	const faceExposure = -maxDiff * scale
	const faceExposureLow = -maxDiffLow * scale
	const faceExposureHigh = -maxDiffHigh * scale
	logColorMatch("!!faceExposure", {
		faceExposure,
		faceExposureLow,
		faceExposureHigh,
		faceLum,
		facePercentiles,
	})

	return {
		faceExposure,
		faceExposureLow,
		faceExposureHigh,
	}
}

export function isGoodFaceColor(faceHsvl: vec4): boolean {
	const [hue, sat, _, lum] = faceHsvl

	if (hue > 0 && hue < 30 / 360 && sat > 0.15 && sat < 0.76 && lum > 0.3 && lum < 0.76) {
		return true
	}

	return false
}

export function isReasonableFaceColor(faceHsvl: vec4): boolean {
	const [hue, sat, _, lum] = faceHsvl

	if (lum > 0.86) {
		return false
	}

	if (hue > 44 / 360 && hue < 250 / 360 && sat > 0.15 && sat < 0.7) {
		return false
	}

	return true
}

export function isFaceTooWhite(faceHsvl: vec4): boolean {
	const sat = faceHsvl[1]
	const lum = faceHsvl[3]
	if (lum - sat > 0.6 && sat < 0.15) {
		return true
	}
	return false
}

/**
 * Step 1 of face tone: normalize face exposure before hue adjustments.
 * If face luminance is far from the expected range, adjust exposure/shadows/
 * highlights to bring it closer. This prevents extreme luminance from biasing
 * the subsequent hue/saturation corrections.
 */
function normalizeFaceExposure(options: {
	faceBefore: Float32Array
	faceWeights: Float32Array
	adjustments: Partial<Adjustments>
	baseRenderOptions: RenderOptions
	afterFaceHSVL: vec4
	refFaceHSVL: vec4 | null
	maxExposure: number
	inputFaceHSVL: vec4
}) {
	const {
		faceBefore,
		faceWeights,
		adjustments,
		baseRenderOptions,
		afterFaceHSVL,
		refFaceHSVL,
		maxExposure,
		inputFaceHSVL,
	} = options
	const result = { ...adjustments }

	const afterFacePixels = renderCpu(faceBefore, adjustments, {
		inputColorSpace: ColorSpaceCpu.sRGB,
		outColorSpace: ColorSpaceCpu.sRGB,
		...baseRenderOptions,
	})

	const { faceExposure, faceExposureLow, faceExposureHigh } = getFaceExposure(
		afterFacePixels,
		faceWeights,
	)

	const aveExp = (faceExposure + faceExposureLow + faceExposureHigh) / 3
	const minExp = Math.min(faceExposure, faceExposureLow, faceExposureHigh)
	let exposureDelta = Math.min(minExp * 0.6 + aveExp * 0.4, faceExposureHigh)

	const initialSign = Math.sign(exposureDelta)
	const inputLum = inputFaceHSVL[3]
	const faceLum = afterFaceHSVL[3]
	const clampedFaceLum = clamp(faceLum, 0.5, clamp(inputLum, 0.65, 0.68))
	const minLumRef = Math.min(Math.max(Math.min(inputLum, faceLum) - 0.05, 0.45), clampedFaceLum)
	const target = refFaceHSVL ? clamp(refFaceHSVL[3], minLumRef, 0.68) : clampedFaceLum
	const targetLum = target * 0.4 + faceLum * 0.3 + Math.min(clampedFaceLum, target) * 0.3
	exposureDelta += Math.min((targetLum - 0.6) * 3, 0)
	if (initialSign !== Math.sign(exposureDelta)) {
		exposureDelta = 0
		return null
	}

	const deltaHS = (adjustments.highlights ?? 0) - (adjustments.shadows ?? 0)
	let thresholdHS = clamp(0.1 - deltaHS * 0.5, 0, 0.1)
	if (exposureDelta < 0) {
		thresholdHS = Math.min(thresholdHS, 0.05)
	}
	const thresholdExp = exposureDelta > 0 ? 0.13 : 0.05
	logColorMatch(
		"[normalizeFaceExposure] exposureDelta",
		exposureDelta,
		"thresholdHS",
		thresholdHS,
		"thresholdExp",
		thresholdExp,
	)

	let updated = false
	if (Math.abs(exposureDelta) > thresholdExp) {
		let deltaExposure =
			(Math.abs(exposureDelta) - thresholdExp) * Math.sign(exposureDelta) * 0.5
		deltaExposure = clamp(getDecreasedValue(deltaExposure, 0.1, 0.5), -0.15, 0.15)
		logColorMatch("[normalizeFaceExposure] deltaExposure", deltaExposure)
		result.exposure = clamp((result.exposure ?? 0) + deltaExposure, -5, maxExposure)
		updated = true
	}

	// positive luminanceOrange often makes face too bright/low-saturated, like washed out, let's reduce it
	if (exposureDelta < 0 && result.luminanceOrange != undefined && result.luminanceOrange > 0.0) {
		logColorMatch("[normalizeFaceExposure] decreasing luminanceOrange", result.luminanceOrange)
		result.luminanceOrange = clamp(result.luminanceOrange + exposureDelta, 0, 1)
		// reducing luminanceOrange also increases saturation, let's reduce orangeSaturation if the face is not too desaturated
		if (
			afterFaceHSVL[1] > 0.25 &&
			result.saturationOrange != undefined &&
			result.saturationOrange > 0.0
		) {
			const luminanceChange = result.luminanceOrange - (adjustments.luminanceOrange ?? 0)
			const deltaSat = Math.max(luminanceChange, (0.25 - afterFaceHSVL[1]) * 2)
			result.saturationOrange = clamp((result.saturationOrange ?? 0) + deltaSat, 0, 1)
		}
		updated = true
	}

	if (Math.abs(exposureDelta) > thresholdHS) {
		let deltaHS = (Math.abs(exposureDelta) - thresholdHS) * Math.sign(exposureDelta)
		deltaHS = clamp(getDecreasedValue(deltaHS, 0.2, 0.5), -0.35, 0.35)
		logColorMatch("[normalizeFaceExposure] deltaHS", deltaHS)
		if (deltaHS < 0) {
			result.highlights = clamp((result.highlights ?? 0) + deltaHS, -1, 1)
			result.whites = clamp((result.whites ?? 0) + deltaHS, -1, 1)
		} else {
			const extraLight = (result.exposure ?? 0) + (result.shadows ?? 0) * 0.5 - maxExposure
			let scale = 1
			if (extraLight > 0) {
				scale = 1.0 - step(extraLight, 0.0, 1) * 0.8
			}
			logColorMatch("[normalizeFaceExposure] shadowsScale", { extraLight, scale })
			deltaHS *= scale * 0.8
			result.shadows = addShadows(result.shadows ?? 0, deltaHS, 0.3)
			const deltaShadows = Math.min(
				(result.shadows ?? 0) - (adjustments.shadows ?? 0),
				result.shadows ?? 0,
			)
			const contrastScale = step(faceLum, 0.08, 0.2) * 0.3 + 0.5
			result.contrast = clamp(
				(result.contrast ?? 0) + Math.max(0, deltaShadows) * contrastScale,
				-1,
				1,
			)
			// blacks
			if (faceLum < 0.25) {
				const deltaBlacks = (0.25 - faceLum) * 2.2
				result.blacks = clamp((result.blacks ?? 0) + deltaBlacks, -1, 1)
			}
		}
		updated = true
	}

	const deltaExposure = (result.exposure ?? 0) - (adjustments.exposure ?? 0)
	const deltaShadows = (result.shadows ?? 0) - (adjustments.shadows ?? 0)
	const deltaBrighten = deltaExposure + deltaShadows * 0.2
	if (deltaBrighten > 0.05) {
		const deltaHighlights = -(deltaBrighten - 0.05) * 1.5
		logColorMatch("[normalizeFaceExposure] deltaHighlights", deltaHighlights)
		result.highlights = clamp((result.highlights ?? 0) + deltaHighlights, -1, 1)
		result.whites = clamp((result.whites ?? 0) + deltaHighlights, -1, 1)
	}

	logColorMatch("[normalizeFaceExposure] result", {
		result: {
			exposure: result.exposure,
			shadows: result.shadows,
			blacks: result.blacks,
			highlights: result.highlights,
			whites: result.whites,
		},
		adjustments: {
			exposure: adjustments.exposure,
			shadows: adjustments.shadows,
			blacks: adjustments.blacks,
			highlights: adjustments.highlights,
			whites: adjustments.whites,
		},
		updated,
	})

	return updated ? result : null
}

function addShadows(initialShadows: number, deltaShadows: number, limit: number = 0.3): number {
	// assume deltaShadows is always positive
	const positiveScale = 0.6
	if (initialShadows > 0) {
		const val = clamp(
			initialShadows + deltaShadows * positiveScale,
			initialShadows - limit,
			initialShadows + limit,
		)
		return clamp(val, -1, 1)
	} else {
		let val = initialShadows + deltaShadows
		if (val > 0) val *= positiveScale
		val = clamp(val, initialShadows - limit, initialShadows + limit)
		return clamp(val, -1, 1)
	}
}

/**
 * Step 2 of face tone: pull parameter-estimated face color closer to the
 * LUT's face result. The neural LUT usually produces a reasonable face color;
 * if the parametric adjustments diverge too much, this closes the gap via
 * HSL orange channel and exposure/shadows tweaks.
 */
export function adjustFaceToneToLutResult(options: {
	faceBefore: Float32Array
	faceWeights: Float32Array
	faceAfter: Float32Array
	afterFaceHSVL?: vec4
	adjustments: Partial<Adjustments>
	baseRenderOptions: RenderOptions
	adjustValue: boolean
	isForAIStyle?: boolean
	inputFaceHSVL?: vec4
}) {
	const {
		faceBefore,
		faceWeights,
		faceAfter,
		adjustments,
		baseRenderOptions,
		adjustValue,
		isForAIStyle,
		inputFaceHSVL,
	} = options
	const result = { ...adjustments }
	let afterFaceHSVL = options.afterFaceHSVL
	if (!afterFaceHSVL) {
		afterFaceHSVL = getAfterFaceHSVL(faceBefore, faceWeights, result, baseRenderOptions)
	}
	const lutedFaceHSVL = getFaceHSVL(faceAfter, faceWeights)
	let hueDiffWithLut = wrapHueDiff(lutedFaceHSVL[0] - afterFaceHSVL[0])
	let satDiffWithLut = lutedFaceHSVL[1] - afterFaceHSVL[1]
	let valDiffWithLut = lutedFaceHSVL[3] - afterFaceHSVL[3]

	// If for AI style, try to reserve original face hue
	if (isForAIStyle && inputFaceHSVL) {
		const afterHue = wrapHueToLarge(afterFaceHSVL[0])
		const inputHue = wrapHueToLarge(inputFaceHSVL[0])
		const lutedHue = wrapHueToLarge(lutedFaceHSVL[0])
		if (afterHue < inputHue && afterHue < lutedHue) {
			hueDiffWithLut = wrapHueDiff(Math.min(lutedHue, inputHue) - afterHue)
		} else if (afterHue > inputHue && afterHue > lutedHue) {
			hueDiffWithLut = wrapHueDiff(Math.max(lutedHue, inputHue) - afterHue)
		} else {
			hueDiffWithLut = 0
		}
		const naturalUnRedHue = Math.min(13 + (afterFaceHSVL[1] - 0.3) * 40, 20) / 360 + 1
		if (inputHue < naturalUnRedHue && lutedHue > inputHue) {
			hueDiffWithLut = Math.max(
				wrapHueDiff(Math.min(lutedHue, naturalUnRedHue) - afterHue),
				hueDiffWithLut,
			)
		}

		// try to not make face too desaturated
		if (satDiffWithLut < 0.0) {
			satDiffWithLut =
				Math.min(
					Math.max(lutedFaceHSVL[1], Math.min(inputFaceHSVL[1], 0.3), 0.3) -
						afterFaceHSVL[1],
					0.0,
				) * 0.5
		}

		// adjust exposure if both luted and input face are brighter or darker than the after face
		const lutedLum = lutedFaceHSVL[3]
		const inputLum = inputFaceHSVL[3]
		const afterLum = afterFaceHSVL[3]

		if (lutedLum > afterLum && inputLum > afterLum) {
			valDiffWithLut = Math.max(0, Math.min(lutedLum, inputLum, 0.65) - afterLum)
		} else if (lutedLum < afterLum && inputLum < afterLum) {
			const isDarkSkin = inputFaceHSVL[1] > 0.7
			valDiffWithLut = Math.min(
				0,
				Math.max(lutedLum, inputLum, isDarkSkin ? 0.35 : 0.48) - afterLum,
			)
		}
	}

	if (
		Math.abs(hueDiffWithLut * 360) > 1.5 ||
		Math.abs(satDiffWithLut) > 0.03 ||
		Math.abs(valDiffWithLut) > 0.03
	) {
		let [deltaHue, deltaSaturation, deltaValue] = getHSLDeltaFromDiffs({
			hsvDiff: [hueDiffWithLut, satDiffWithLut, valDiffWithLut],
			color: "orange",
			scale: 0.7,
			satLumScale: 0.05,
			finalHueScale: 0.7,
			limit: 0.3,
		})

		const afterHue = afterFaceHSVL[0]

		// if face is reddish, reduce tint, don't increase saturation if face is not too desaturated
		// if it's very low saturation, don't reduce tint, but increase temperature for natural more orange face tone
		let isOrange = true
		if ((afterHue * 360 < 4 || afterHue * 360 > 230) && hueDiffWithLut > 0.0) {
			const afterSat = afterFaceHSVL[1]
			const tintScale = clamp((afterSat - 0.17) / 0.1, 0, 1)
			const deltaTint = clamp(-0.7 * hueDiffWithLut * 360, -10, 10) * tintScale
			logColorMatch("[adjustFaceToneToLutResult] deltaTint", {
				deltaTint,
				tintScale,
				afterSat,
			})
			result.tint = clamp((result.tint ?? 0) + deltaTint, -100, 100)
			if (afterSat < 0.17) {
				const tempScale = clamp((0.17 - afterSat) / 0.07, 0, 1)
				const deltaTemp = clamp(hueDiffWithLut * 360 * 2, 0, 200) * tempScale
				result.temperature = (result.temperature ?? 0) + deltaTemp
				logColorMatch("[adjustFaceToneToLutResult] deltaTemp", {
					deltaTemp,
					tempScale,
					afterSat,
				})
			} else {
				isOrange = false
			}
			if (afterFaceHSVL[1] > 0.25) {
				deltaSaturation = Math.min(deltaSaturation, 0.0)
			}
		} else {
			deltaHue = getDecreasedValue(deltaHue, 0.12, 0.5)
			result.hueOrange = clamp((result.hueOrange ?? 0) + deltaHue, -1, 1)
		}

		deltaSaturation = getDecreasedValue(deltaSaturation, 0.08, 0.5)
		deltaSaturation = clamp(deltaSaturation, -0.12, 0.12)
		result.saturationOrange = clamp((result.saturationOrange ?? 0) + deltaSaturation, -1, 1)

		if (adjustValue && isOrange) {
			deltaValue = getDecreasedValue(deltaValue * 0.5, 0.08, 0.5)
			deltaValue = clamp(deltaValue, -0.1, 0.1)
			result.luminanceOrange = clamp((result.luminanceOrange ?? 0) + deltaValue, -1, 1)
		} else if (inputFaceHSVL) {
			// adjust exposure if both luted and input face are brighter or darker than the after face
			const deltaExposure = clamp(valDiffWithLut * 3, -0.15, 0.25)
			let shadowsScale = 0.0
			if (deltaExposure > 0 && result.shadows && result.shadows < 0.5) {
				shadowsScale = Math.min(0.3 + (0.5 - result.shadows) * 0.5, 0.6)
			}
			result.exposure = clamp(
				(result.exposure ?? 0) + deltaExposure * (1 - shadowsScale),
				-5,
				5,
			)
			result.shadows = clamp((result.shadows ?? 0) + deltaExposure * shadowsScale, -1, 1)
			logColorMatch("[adjustFaceToneToLutResult] deltaExposure", deltaExposure)
		}

		logColorMatch("[adjustFaceToneToLutResult] info", {
			diff: [hueDiffWithLut * 360, satDiffWithLut, valDiffWithLut],
			delta: [deltaHue, deltaSaturation, deltaValue],
			afterFaceHSVL: [
				afterFaceHSVL[0] * 360,
				afterFaceHSVL[1],
				afterFaceHSVL[2],
				afterFaceHSVL[3],
			],
			lutedFaceHSVL: [
				lutedFaceHSVL[0] * 360,
				lutedFaceHSVL[1],
				lutedFaceHSVL[2],
				lutedFaceHSVL[3],
			],
		})

		return { result, lutedFaceHSVL }
	}

	return { result: null, lutedFaceHSVL: null }
}

function clampToRange(
	value: number | undefined,
	initialValue: number | undefined,
	limits: vec2,
	finalLimit: number = 1,
): number {
	const [limitLow, limitHigh] = limits
	const min = Math.max((initialValue ?? 0) + limitLow, -finalLimit)
	const max = Math.min((initialValue ?? 0) + limitHigh, finalLimit)
	return clamp(value ?? 0, min, max)
}
